import { Notification } from '@components/prereg_validation/notification'
import AddIcon from '@mui/icons-material/Add'
import { Button, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useSessionContext } from '@providers/session'
import { buildErrorMessage } from '@shared/error'
import { i18WithParams as t } from '@shared/locale'
import { WalletBalance } from '@views/profile/payment/wallet/wallet_balance'
import { AddCartPaymentForm, SimpleModal } from 'components-ui'
import { Spacer } from 'components-ui/src/spacer/spacer'
import { enqueueSnackbar } from 'notistack'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Hooks } from 'service-api'
import { ClientStoredPaymentMethodsService } from 'service-api/src/services/client_stored_payment_methods.service'
import { captureError } from 'service-api/src/shared/monitoring'
import { OrderPaymentAdminDtoEx } from 'service-api/src/types/api'
import { useWalletBalance } from '../../../hooks/use_wallet_balance'
import { PaymentMethods } from './cards/payment_methods'

export const PaymentWallet = () => {
  const sessionCtx = useSessionContext()
  const { accountName: locationName = '' } = useParams()
  const theme = useTheme()
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))
  const lupId = sessionCtx.state.user?.lupId || ''
  const { walletBalance, isFetching: fetchingWalletBalance } = useWalletBalance(lupId, { cacheEnabled: true })
  const { data: locationUserProfile } = Hooks.useClientLocationUserProfile(locationName, lupId || '')
  const {
    data: storedPaymentMethodsResponse,
    isLoading: isLoadingStoredPaymentMethods,
    refetch,
  } = Hooks.useClientStoredPaymentMethods(locationName)
  const [orderPayment, setOrderPayment] = useState<OrderPaymentAdminDtoEx>()

  const onDeleteReFetchStoredPaymentMethods = async () => {
    await refetch()
  }

  const handleAddCard = async () => {
    try {
      if (locationUserProfile) {
        const payment = await ClientStoredPaymentMethodsService.create(locationName, locationUserProfile.id!, {
          locationUserProfileId: locationUserProfile.id || '',
          payFacType: 'ADYEN',
        })
        setOrderPayment(payment)
      }
    } catch (error) {
      enqueueSnackbar(buildErrorMessage(error), { variant: 'error' })
      captureError(error as Error)
    }
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Notification lupId={sessionCtx.state.user?.lupId || ''} />

      <Typography variant="h2" sx={{ mb: 2 }} pl={isBelowMD ? 2 : 0}>
        {t('profile.paymentMethod.title')}
      </Typography>
      <Spacer height={3} />

      {fetchingWalletBalance ? (
        <Skeleton variant="rectangular" width="100%" height={200} />
      ) : (
        <>
          {walletBalance > 0 && <WalletBalance walletBalanceTotal={walletBalance} />}
          <PaymentMethods
            storedPaymentMethods={storedPaymentMethodsResponse ?? []}
            isLoadingStoredPaymentMethods={isLoadingStoredPaymentMethods}
            onDeleteReFetchStoredPaymentMethods={onDeleteReFetchStoredPaymentMethods}
          />
          <Box mt={2}>
            <Button variant="text" startIcon={<AddIcon />} onClick={handleAddCard}>
              {t('profile.paymentMethod.addCard')}
            </Button>
          </Box>
          <SimpleModal open={!!orderPayment} sx={{ width: '550px', p: 2, height: '550px', overflow: 'scroll' }}>
            {locationUserProfile && (
              <AddCartPaymentForm
                orderPayment={orderPayment!}
                locationUserProfile={locationUserProfile}
                handleCancelPayment={async () => {
                  await ClientStoredPaymentMethodsService.cancel(
                    locationName,
                    locationUserProfile?.id || '',
                    orderPayment?.payment?.id || ''
                  )
                  setOrderPayment(undefined)
                }}
                handleConfirmPayment={async (result) => {
                  await ClientStoredPaymentMethodsService.confirm(
                    locationName,
                    locationUserProfile?.id || '',
                    orderPayment?.payment?.id || '',
                    {
                      sessionResult: result.sessionResult,
                    }
                  )
                  enqueueSnackbar(t('storedPaymentMethod.success'), { variant: 'success' })
                  setOrderPayment(undefined)
                  refetch?.()
                }}
                isAdmin={false}
              />
            )}
          </SimpleModal>
        </>
      )}
    </Box>
  )
}
